import { request } from "./http";
import { readStoredAuth } from "../auth/authStorage";

// --- paging helpers ---
function normalizePagedResponse(res, { page = 0, size = 12 } = {}) {
    if (res && typeof res === "object" && Array.isArray(res.content)) {
        const p = Number.isFinite(Number(res.number)) ? Number(res.number) : page;
        const s = Number.isFinite(Number(res.size)) ? Number(res.size) : size;
        const totalElements = Number.isFinite(Number(res.totalElements))
            ? Number(res.totalElements)
            : Array.isArray(res.content)
            ? res.content.length
            : 0;
        const totalPages = Number.isFinite(Number(res.totalPages))
            ? Number(res.totalPages)
            : Math.max(1, Math.ceil(totalElements / (s || 1)));
        return { items: res.content, page: p, size: s, totalElements, totalPages, hasMore: p + 1 < totalPages };
    }

    if (res && typeof res === "object" && Array.isArray(res.items)) {
        const totalElements = res.items.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / (size || 1)));
        const start = page * size;
        const end = start + size;
        return { items: res.items.slice(start, end), page, size, totalElements, totalPages, hasMore: end < totalElements };
    }

    if (Array.isArray(res)) {
        const totalElements = res.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / (size || 1)));
        const start = page * size;
        const end = start + size;
        return { items: res.slice(start, end), page, size, totalElements, totalPages, hasMore: end < totalElements };
    }

    return { items: [], page, size, totalElements: 0, totalPages: 1, hasMore: false };
}

let _cacheAll = null;
let _cacheAt = 0;
let _cacheOwnerKey = "";
const CACHE_TTL_MS = 30_000;

function currentUserCacheKey() {
    const { id, role, email } = resolveCurrentUserMeta();
    return `${String(id || "anon")}|${String(role || "").toUpperCase()}|${String(email || "").toLowerCase()}`;
}

async function listAllCached() {
    const now = Date.now();
    const ownerKey = currentUserCacheKey();
    if (_cacheAll && _cacheOwnerKey === ownerKey && now - _cacheAt < CACHE_TTL_MS) return _cacheAll;
    const all = await (async () => {
        try {
            return await appointmentsApi.listMyAll();
        } catch {
            return [];
        }
    })();
    _cacheAll = Array.isArray(all) ? all : [];
    _cacheAt = now;
    _cacheOwnerKey = ownerKey;
    return _cacheAll;
}

function invalidateAppointmentsCache() {
    _cacheAll = null;
    _cacheAt = 0;
    _cacheOwnerKey = "";
}

// Local fallback (if backend doesn't expose joined group sessions in /api/me/appointments yet)
const LS_GROUP_JOIN_PREFIX = "gs:joined:"; // gs:joined:<sessionId> -> JSON { sessionId, bookingId?, joinedAt }

function safeJsonParse(s) {
    try {
        return JSON.parse(s);
    } catch {
        return null;
    }
}

function uniqBy(arr, keyFn) {
    const out = [];
    const seen = new Set();
    for (const x of arr || []) {
        const k = keyFn(x);
        if (k == null) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(x);
    }
    return out;
}

async function tryGetArray(url) {
    try {
        const res = await request(url, { method: "GET" });
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.content)) return res.content;
        if (Array.isArray(res?.items)) return res.items;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    } catch (e) {
        // ignore 404/403 etc — fallback only
        return [];
    }
}

function resolveCurrentUserMeta() {
    const me = readStoredAuth()?.me;
    const id = Number(me?.id ?? me?.userId ?? me?.user_id ?? 0);
    const roleRaw = String(me?.role ?? me?.roleTitle ?? me?.roleName ?? "").trim().toUpperCase();
    const role = roleRaw.startsWith("ROLE_") ? roleRaw.slice(5) : roleRaw;
    const email = String(me?.email ?? me?.mail ?? "").trim().toLowerCase();
    const name = String(me?.name ?? me?.fullName ?? me?.login ?? "").trim().toLowerCase();
    return {
        id: Number.isFinite(id) && id > 0 ? id : null,
        role,
        email,
        name,
    };
}

function toNumId(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeLower(value) {
    return String(value ?? "").trim().toLowerCase();
}

function extractNumberIdsFromArray(value) {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => {
            if (item && typeof item === "object") {
                return (
                    toNumId(item.userId) ??
                    toNumId(item.user_id) ??
                    toNumId(item.id) ??
                    toNumId(item.clientId) ??
                    toNumId(item.client_id) ??
                    toNumId(item.psychologistId) ??
                    toNumId(item.psychologist_id) ??
                    null
                );
            }
            return toNumId(item);
        })
        .filter(Boolean);
}

function extractOwnerIds(item) {
    const source = item && typeof item === "object" ? item : {};
    const ids = [
        source.clientId,
        source.client_id,
        source.clientUserId,
        source.client_user_id,
        source.client?.id,
        source.client?.userId,
        source.client?.user_id,
        source.psychologistId,
        source.psychologist_id,
        source.psychologistUserId,
        source.psychologist_user_id,
        source.psychologist?.id,
        source.psychologist?.userId,
        source.psychologist?.user_id,
        source.hostId,
        source.host_id,
        source.hostUserId,
        source.host_user_id,
        source.ownerId,
        source.owner_id,
        source.userId,
        source.user_id,
        source.createdById,
        source.created_by_id,
        source.therapistId,
        source.therapist_id,
        source.specialistId,
        source.specialist_id,
    ]
        .map(toNumId)
        .filter(Boolean);

    const participantIds = [
        ...extractNumberIdsFromArray(source.participants),
        ...extractNumberIdsFromArray(source.members),
        ...extractNumberIdsFromArray(source.attendees),
        ...extractNumberIdsFromArray(source.clients),
        ...extractNumberIdsFromArray(source.users),
    ];

    return new Set([...ids, ...participantIds]);
}

function extractOwnerEmails(item) {
    const source = item && typeof item === "object" ? item : {};
    const values = [
        source.clientEmail,
        source.client_email,
        source.client?.email,
        source.psychologistEmail,
        source.psychologist_email,
        source.psychologist?.email,
        source.hostEmail,
        source.host_email,
        source.email,
        source.userEmail,
        source.user_email,
    ]
        .map(normalizeLower)
        .filter(Boolean);

    const participantEmails = (Array.isArray(source.participants) ? source.participants : [])
        .map((p) => normalizeLower(p?.email))
        .filter(Boolean);

    return new Set([...values, ...participantEmails]);
}

function extractOwnerNames(item) {
    const source = item && typeof item === "object" ? item : {};
    const values = [
        source.clientName,
        source.client_name,
        source.client?.name,
        source.psychologistName,
        source.psychologist_name,
        source.psychologist?.name,
        source.hostName,
        source.host_name,
        source.userName,
        source.user_name,
        source.participantName,
        source.participant_name,
    ]
        .map(normalizeLower)
        .filter(Boolean);

    const participantNames = (Array.isArray(source.participants) ? source.participants : [])
        .map((p) => normalizeLower(p?.name || p?.fullName || p?.displayName))
        .filter(Boolean);

    return new Set([...values, ...participantNames]);
}

function isOwnedByCurrentUser(item, meta) {
    const source = item && typeof item === "object" ? item : {};
    if (source?._local === true) return true;

    const myId = toNumId(meta?.id);
    const myEmail = normalizeLower(meta?.email);
    const myName = normalizeLower(meta?.name);

    const ownerIds = extractOwnerIds(source);
    if (myId && ownerIds.has(myId)) return true;

    const ownerEmails = extractOwnerEmails(source);
    if (myEmail && ownerEmails.has(myEmail)) return true;

    const ownerNames = extractOwnerNames(source);
    if (myName && ownerNames.has(myName)) return true;

    return false;
}

function extractPsychologistOwnerIds(item) {
    const source = item && typeof item === "object" ? item : {};
    return new Set(
        [
            source.psychologistId,
            source.psychologist_id,
            source.psychologistUserId,
            source.psychologist_user_id,
            source.psychologist?.id,
            source.psychologist?.userId,
            source.psychologist?.user_id,
            source.therapistId,
            source.therapist_id,
            source.specialistId,
            source.specialist_id,
            source.hostId,
            source.host_id,
            source.hostUserId,
            source.host_user_id,
            source.ownerId,
            source.owner_id,
            source.createdById,
            source.created_by_id,
        ]
            .map(toNumId)
            .filter(Boolean)
    );
}

function isoDateOnly(value) {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export const appointmentsApi = {
    /**
     * Individual appointments (legacy): GET /api/me/appointments
     */
    listMy: () => request("/api/me/appointments", { method: "GET" }),

    /**
     * Best-effort: try to load group session bookings from additional endpoints (if they exist)
     */
    listMyGroup: async () => {
        const candidates = [
            "/api/me/group-appointments",
            "/api/me/group-bookings",
            "/api/me/group-session-bookings",
            "/api/me/group-sessions?page=0&size=200",
            "/api/me/group-sessions",
        ];
        for (const url of candidates) {
            const arr = await tryGetArray(url);
            if (arr && arr.length) return arr;
        }
        return [];
    },

    /**
     * PRIVATE list for psychologist/admin/moderator by psychologist user id.
     * Endpoint expects date range.
     */
    listAsPsychologist: async () => {
        const { id, role } = resolveCurrentUserMeta();
        if (!id) return [];
        if (!["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(role)) return [];

        const now = new Date();
        const from = new Date(now.getTime());
        const to = new Date(now.getTime());
        from.setUTCDate(from.getUTCDate() - 365);
        to.setUTCDate(to.getUTCDate() + 365);

        const fromStr = isoDateOnly(from);
        const toStr = isoDateOnly(to);
        if (!fromStr || !toStr) return [];

        const res = await request(
            `/api/psychologists/${encodeURIComponent(String(id))}/bookings?from=${encodeURIComponent(fromStr)}&to=${encodeURIComponent(toStr)}`,
            { method: "GET" }
        );
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.items)) return res.items;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    },

    /**
     * Unified list: individual + group (api + local fallback)
     */
    listMyAll: async () => {
        const meta = resolveCurrentUserMeta();
        const [baseRaw, groupRaw, psychRaw] = await Promise.all([
            appointmentsApi.listMy().catch(() => []),
            appointmentsApi.listMyGroup().catch(() => []),
            appointmentsApi.listAsPsychologist().catch(() => []),
        ]);

        const base = (Array.isArray(baseRaw) ? baseRaw : Array.isArray(baseRaw?.items) ? baseRaw.items : [])
            .map((item) => ({ ...(item || {}), __src: "me" }));
        const group = (Array.isArray(groupRaw) ? groupRaw : [])
            .map((item) => ({ ...(item || {}), __src: "me_group" }));
        const psych = (Array.isArray(psychRaw) ? psychRaw : [])
            .map((item) => ({ ...(item || {}), __src: "psych" }));

        const localGroup = appointmentsApi.listLocalGroupJoins()
            .map((item) => ({ ...(item || {}), __src: "local" }));

        // Merge by bookingId/type or by groupSessionId
        const merged = uniqBy(
            [...base, ...group, ...psych, ...localGroup],
            (a) => {
                const id = a?.id ?? a?.bookingId ?? a?.booking_id ?? null;
                const t = String(a?.type || a?.appointmentType || "").toUpperCase();
                const gs = a?.groupSessionId ?? a?.sessionId ?? a?.group_session_id ?? null;
                return id != null ? `${t}:${id}` : gs != null ? `${t || "GROUP"}:gs:${gs}` : null;
            }
        );

        // Visibility gate:
        // - /api/me/* sources are already user-scoped, keep them as-is;
        // - psychologist source is additionally filtered by ownership when signals exist.
        return merged
            .filter((item) => {
                const src = String(item?.__src || "");
                if (src === "me" || src === "me_group" || src === "local") return true;
                if (src === "psych") {
                    const myId = toNumId(meta?.id);
                    const ownerIds = extractPsychologistOwnerIds(item);
                    if (myId && ownerIds.size > 0) return ownerIds.has(myId);
                    // Endpoint is already scoped by /api/psychologists/{me.id}/bookings.
                    // If psychologist/host owner id is absent in payload, trust endpoint scope.
                    return true;
                }
                return isOwnedByCurrentUser(item, meta);
            })
            .map((item) => {
                if (!item || typeof item !== "object") return item;
                const { __src, ...rest } = item;
                return rest;
            });
    },

    /**
     * Пагинированный unified список (готово к бэку). Сейчас:
     *  - пробуем /api/me/appointments/all/page (если появится),
     *  - иначе грузим все и режем на фронте.
     */
    listMyAllPaged: async ({ page = 0, size = 12 } = {}) => {
        const qs = new URLSearchParams();
        qs.set("page", String(page));
        qs.set("size", String(size));

        // 1) server-side paging (future)
        try {
            const res = await request(`/api/me/appointments/all/page?${qs.toString()}`, { method: "GET" });
            return normalizePagedResponse(res, { page, size });
        } catch {
            // ignore
        }

        // 2) client-side paging fallback
        const all = await listAllCached();
        const sorted = [...(all || [])].sort((a, b) => {
            const da = new Date(a?.startDateTime || a?.start_datetime || 0).getTime() || 0;
            const db = new Date(b?.startDateTime || b?.start_datetime || 0).getTime() || 0;
            // newest first
            return db - da;
        });
        return normalizePagedResponse(sorted, { page, size });
    },

    invalidateCache: invalidateAppointmentsCache,

    /**
     * Local fallback for joined group sessions (written on join flow)
     */
    rememberLocalGroupJoin: (sessionId, bookingId, meta = {}) => {
        try {
            const sid = String(sessionId ?? "").trim();
            if (!sid) return;
            const payload = {
                sessionId: sid,
                bookingId: bookingId != null ? String(bookingId) : null,
                joinedAt: Date.now(),
                type: "GROUP",
                title: meta?.title || meta?.sessionTitle || null,
                coverUrl: meta?.coverUrl || meta?.coverImg || null,
                status: meta?.status || "PENDING_PAYMENT",
                expiresAt: meta?.expiresAt || (Date.now() + 15 * 60 * 1000),
            };
            localStorage.setItem(LS_GROUP_JOIN_PREFIX + sid, JSON.stringify(payload));
        } catch {}
    },

    listLocalGroupJoins: () => {
        try {
            const out = [];
            for (let i = 0; i < localStorage.length; i += 1) {
                const k = localStorage.key(i);
                if (!k || !k.startsWith(LS_GROUP_JOIN_PREFIX)) continue;
                const raw = safeJsonParse(localStorage.getItem(k) || "");
                if (!raw?.sessionId) continue;

                // auto-expire non-paid reservations from localStorage
                const st = String(raw?.status || "").toUpperCase();
                const exp = Number(raw?.expiresAt || 0);
                if (exp && Date.now() > exp && st !== "PAID") {
                    try { localStorage.removeItem(k); } catch {}
                    continue;
                }


                // pseudo appointment DTO (will be enriched by UI when needed)
                out.push({
                    id: raw.bookingId || `gs_${raw.sessionId}`,
                    type: "GROUP",
                    groupSessionId: raw.sessionId,
                    sessionTitle: raw.title || "Групповая сессия",
                    sessionCoverUrl: raw.coverUrl || null,
                    status: raw.status || "PENDING_PAYMENT",
                    // timestamps might be unknown here; UI will fetch details
                    _local: true,
                });
            }
            return out;
        } catch {
            return [];
        }
    },

    createPayment: (bookingId) => {
        invalidateAppointmentsCache();
        return request("/payments/yookassa/create", {
            method: "POST",
            json: { bookingId },
        });
    },

    getOne: (id) => {
        const bookingId = String(id ?? "").trim();
        if (!bookingId) throw new Error("bookingId is required");
        return request(`/api/me/appointments/${bookingId}`, { method: "GET" });
    },
};
